import React, {ReactElement} from 'react';
import {Skeleton} from '@mui/material';
import {AlertWarningIcon, Button, Typography} from '@customink/pigment-react';
import CopyLinkTextButton from '../CopyLinkTextButton';
import {selectPrimaryCampaign} from '../../slices/storesSlice';
import services from '../../api/remoteServices';
import {
  useGetOrderSummaryQuery,
  useGetStoreAddressesQuery,
  useGetStoreDetailsQuery
} from '../../api/storesApi';
import {useAppSelector, useDashboardMessage, useStoreType} from '../../hooks';
import './index.scss';
import {CampaignAttributes, ShippingType} from '../../models/storeDetails';

type DashboardMessageProps = {
  storeSlug: string;
};

type GroupOrderMessageProps = {
  storeSlug: string;
  primaryCampaign: CampaignAttributes;
};

const GroupOrderMessage: React.FC<GroupOrderMessageProps> = ({
  storeSlug,
  primaryCampaign
}: GroupOrderMessageProps) => {
  const storeDetails = useAppSelector(state => state.stores.storeDetails);
  const dashboardMessage = useDashboardMessage();
  const {isStoreTypeGroupOrder} = useStoreType();

  const showShare =
    storeDetails?.status === 'open' &&
    (primaryCampaign ? primaryCampaign?.status === 'active' : true);
  const storeUrl = isStoreTypeGroupOrder
    ? `${services.storefront}/${storeSlug}`
    : `${services.stores}/${storeSlug}`;

  return (
    <div className="dashboard-message">
      <div className="message-title">
        <div className="info-icon">{dashboardMessage.icon}</div>
        <Typography className="bolder title-text" variant="subHeading">
          {dashboardMessage.title}
        </Typography>
      </div>
      {dashboardMessage.message && (
        <div className="message-content section">
          <Typography variant="bodyShort2">
            {dashboardMessage.message}
          </Typography>
        </div>
      )}
      {showShare ? (
        <div className="message-share section" aria-label="Store Share">
          <Typography variant="bodyShort2" className="share-text">
            Share your link: <span className="bolder">{storeUrl}</span>
          </Typography>
          <CopyLinkTextButton copyLink={storeUrl} />
        </div>
      ) : null}
    </div>
  );
};

const HybridMessage: React.FC<DashboardMessageProps> = ({
  storeSlug
}: DashboardMessageProps) => {
  const storeSettingsUrl = `${services.storefront}/${storeSlug}/manage/settings?tab=1#shipping-details`;

  return (
    <div className="dashboard-message" style={{marginBottom: '1.5rem'}}>
      <div className="message-title">
        <div className="info-icon">
          <AlertWarningIcon color="warning" fontSize="medium" />
        </div>
        <Typography className="bolder title-text" variant="subHeading">
          Enter Your Shipping Address.
        </Typography>
      </div>
      <div className="message-content section">
        <Typography variant="bodyShort2">
          There&apos;s one more step to complete before you start collecting
          purchases. Go to your settings page and enter your shipping address so
          supporters may choose to ship to your location for free.
        </Typography>
      </div>
      <div className="message-share section" aria-label="Store Share">
        <Button
          className="viewBtn"
          variant="primary"
          href={storeSettingsUrl}
          role="link">
          Enter Your Shipping Address
        </Button>
      </div>
    </div>
  );
};

const DashboardMessage: React.FC<DashboardMessageProps> = ({
  storeSlug
}: DashboardMessageProps): ReactElement => {
  const {isLoading: storeDetailsIsLoading} = useGetStoreDetailsQuery(storeSlug);
  const storeDetails = useAppSelector(state => state.stores.storeDetails);
  const primaryCampaign = useAppSelector(selectPrimaryCampaign);
  const {isLoading: orderSummaryIsLoading} = useGetOrderSummaryQuery({
    urlSlug: storeSlug,
    campaignId: primaryCampaign.id
  });
  const {data: storeAddressesData = [], isLoading: isStoreAddressesLoading} =
    useGetStoreAddressesQuery({
      storeSlug
    });

  const showHybridMessage =
    storeDetails?.shippingType === ShippingType.HybridShipping &&
    storeAddressesData.length <= 0;

  if (
    storeDetailsIsLoading ||
    orderSummaryIsLoading ||
    isStoreAddressesLoading
  ) {
    return <Skeleton variant="rectangular" width="100%" height={180} />;
  }

  return showHybridMessage ? (
    <HybridMessage storeSlug={storeSlug} />
  ) : (
    <GroupOrderMessage
      storeSlug={storeSlug}
      primaryCampaign={primaryCampaign}
    />
  );
};

export default DashboardMessage;
